import React from 'react';
import { ApolloError } from '@apollo/client';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  IPaginationProps,
  LinearProgress,
  StatusChip,
  StatusChipVariant,
  LoadedContent,
} from '@occc-app/design-system';
import { sentenceCase } from '@occc-app/lib/strings';
import { Membership_State, MembershipItemFragment } from '@occc/graphql';

import { formatPhoneNumberNationalForUS } from '@occc/utils-phone-number';
import { displayFullName } from '../utils';

interface MembersTableProps {
  data?: MembershipItemFragment[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  onClickMember: (member: MembershipItemFragment) => void;
}

interface MemberStateMapItem {
  label: string;
  variant: StatusChipVariant;
}

type MemberStateMap = {
  [key in Membership_State]: MemberStateMapItem;
};

const memberStateMap: MemberStateMap = {
  [Membership_State.NewMember]: {
    label: sentenceCase(Membership_State.NewMember),
    variant: 'info',
  },
  [Membership_State.Active]: {
    label: sentenceCase(Membership_State.Active),
    variant: 'success',
  },
  [Membership_State.Paused]: {
    label: sentenceCase(Membership_State.Paused),
    variant: 'default',
  },
  [Membership_State.Suspended]: {
    label: sentenceCase(Membership_State.Suspended),
    variant: 'warning',
  },
  [Membership_State.Deleted]: {
    label: sentenceCase(Membership_State.Deleted),
    variant: 'warning',
  },
  [Membership_State.DeletionPlaceholder]: {
    label: sentenceCase(Membership_State.DeletionPlaceholder),
    variant: 'warning',
  },
};

export const MembersTable: React.FC<MembersTableProps> = ({
  data,
  error,
  loading,
  paginationProps,
  onClickMember,
}) => {
  const renderData = (members: MembershipItemFragment[]) => {
    const rows = members.map((member) => {
      const onClick: React.MouseEventHandler<HTMLElement> = (event) => {
        event.stopPropagation();
        onClickMember(member);
      };

      const memberState = member.state ? (
        <StatusChip
          label={memberStateMap[member.state].label}
          variant={memberStateMap[member.state].variant}
        />
      ) : null;

      return (
        <TableRow key={member.id} onClick={onClick}>
          <TableCell>{displayFullName(member)}</TableCell>
          <TableCell>{member.email ?? ''}</TableCell>
          <TableCell>{formatPhoneNumberNationalForUS(member.phone || '')}</TableCell>
          <TableCell>{member.points}</TableCell>
          <TableCell>{memberState}</TableCell>
        </TableRow>
      );
    });

    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Points</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<MembershipItemFragment>
      error={error}
      loading={loading}
      items={data}
      renderData={renderData}
    />
  );
};
